import { useState } from 'react';

import { Button, Dropdown, FieldLabel, Modal, showSnackbar, Table, TextArea, TextInput } from 'fluxo-ui';
import * as fluxoIcons from 'fluxo-ui/icons';
import { EditIcon, PlusIcon, SaveIcon, TimesIcon, TrashIcon } from 'fluxo-ui/icons';

import type { ComponentEvent, ListItem } from 'fluxo-ui';

const iconNameOptions: ListItem[] = Object.keys(fluxoIcons)
    .filter((key) => key.endsWith('Icon'))
    .sort()
    .map((key) => ({ label: key.replace(/Icon$/, ''), value: key }));

export interface MasterListItem {
    _id?: string;
    name: string;
    code: string;
    description?: string;
    parentId?: string;
    displayOrder?: number;
    isActive?: boolean;
    metadata?: Record<string, any>;
}

interface MasterListEditorProps {
    title: string;
    description?: string;
    items: MasterListItem[];
    hierarchical?: boolean;
    onSave: (item: MasterListItem) => Promise<void>;
    onDelete: (itemId: string) => Promise<void>;
    onRefresh: () => Promise<void>;
    loading?: boolean;
    customFields?: Array<{
        key: string;
        label: string;
        type: 'text' | 'textarea' | 'number' | 'dropdown' | 'color' | 'icon';
        options?: ListItem[];
        required?: boolean;
    }>;
}

const MasterListEditor: React.FC<MasterListEditorProps> = ({
    title,
    description,
    items,
    hierarchical = false,
    onSave,
    onDelete,
    onRefresh,
    loading = false,
    customFields = [],
}) => {
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState<MasterListItem | null>(null);
    const [formData, setFormData] = useState<MasterListItem>({
        name: '',
        code: '',
        description: '',
        isActive: true,
    });
    const [saving, setSaving] = useState(false);

    const resetForm = () => {
        setFormData({
            name: '',
            code: '',
            description: '',
            isActive: true,
        });
        setEditingItem(null);
    };

    const handleEdit = (item: MasterListItem) => {
        setEditingItem(item);
        setFormData({ ...item });
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            showSnackbar('Name is required', 'Validation Error', { type: 'error' });
            return;
        }

        if (!formData.code.trim()) {
            showSnackbar('Code is required', 'Validation Error', { type: 'error' });
            return;
        }

        setSaving(true);
        try {
            await onSave(formData);
            showSnackbar(`${title} ${editingItem ? 'updated' : 'created'} successfully`, 'Success', { type: 'success' });
            setShowModal(false);
            resetForm();
            await onRefresh();
        } catch (error: any) {
            showSnackbar(error.message || `Failed to save ${title}`, 'Error', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (itemId: string) => {
        if (!window.confirm('Are you sure you want to delete this item?')) {
            return;
        }

        try {
            await onDelete(itemId);
            showSnackbar(`${title} deleted successfully`, 'Success', { type: 'success' });
            await onRefresh();
        } catch (error: any) {
            showSnackbar(error.message || `Failed to delete ${title}`, 'Error', { type: 'error' });
        }
    };

    const parentOptions: ListItem[] = hierarchical
        ? [{ label: 'None (Root Level)', value: '' }, ...items.map((item) => ({ label: item.name, value: item._id || '' }))]
        : [];

    const buildHierarchy = (items: MasterListItem[], parentId: string | undefined = undefined, level: number = 0): MasterListItem[] => {
        const result: MasterListItem[] = [];
        items
            .filter((item) => item.parentId === parentId)
            .forEach((item) => {
                result.push({ ...item, displayOrder: level });
                result.push(...buildHierarchy(items, item._id, level + 1));
            });
        return result;
    };

    const displayItems = hierarchical ? buildHierarchy(items) : items;

    const columns = [
        {
            field: 'name',
            title: 'Name',
            template: (item: MasterListItem) => (
                <div style={{ paddingLeft: `${(item.displayOrder || 0) * 20}px` }}>
                    {(item.displayOrder || 0) > 0 && <span className="text-slate-400 mr-2">└─</span>}
                    <span className="font-medium text-slate-900 dark:text-white">{item.name}</span>
                </div>
            ),
        },
        {
            field: 'code',
            title: 'Code',
            template: (item: MasterListItem) => <code className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-sm">{item.code}</code>,
        },
        {
            field: 'description',
            title: 'Description',
            template: (item: MasterListItem) => <span className="text-slate-600 dark:text-slate-400 text-sm">{item.description || '-'}</span>,
        },
        {
            field: 'isActive',
            title: 'Status',
            template: (item: MasterListItem) => (
                <span
                    className={`px-2 py-1 text-xs rounded ${
                        item.isActive
                            ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                            : 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300'
                    }`}
                >
                    {item.isActive ? 'Active' : 'Inactive'}
                </span>
            ),
        },
        {
            field: 'actions',
            title: 'Actions',
            template: (item: MasterListItem) => (
                <div className="flex items-center gap-2">
                    <Button leftIcon={EditIcon} variant="default" size="sm" layout="plain" onClick={() => handleEdit(item)} />
                    <Button leftIcon={TrashIcon} variant="danger" size="sm" layout="plain" onClick={() => handleDelete(item._id!)} />
                </div>
            ),
        },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{title}</h2>
                    {description && <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{description}</p>}
                </div>
                <Button
                    label={`Add ${title}`}
                    leftIcon={PlusIcon}
                    variant="primary"
                    onClick={() => {
                        resetForm();
                        setShowModal(true);
                    }}
                />
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                <Table
                    rows={displayItems}
                    totalRows={displayItems.length}
                    columns={columns}
                    isLoading={loading}
                    noRowsMessage={`No ${title.toLowerCase()} found`}
                />
            </div>

            {/* Add/Edit Modal */}
            <Modal
                isOpen={showModal}
                onClose={() => !saving && setShowModal(false)}
                title={editingItem ? `Edit ${title}` : `Add ${title}`}
                size="md"
            >
                <div className="space-y-4">
                    <FieldLabel label="Name" required>
                        <TextInput
                            value={formData.name}
                            onChange={(e: ComponentEvent<string>) => setFormData({ ...formData, name: e.value })}
                            placeholder="Enter name"
                            disabled={saving}
                        />
                    </FieldLabel>

                    <FieldLabel label="Code" required hint="Unique identifier (e.g., tech, prog_lang)">
                        <TextInput
                            value={formData.code}
                            onChange={(e: ComponentEvent<string>) => setFormData({ ...formData, code: e.value })}
                            placeholder="Enter code"
                            disabled={saving || !!editingItem}
                        />
                    </FieldLabel>

                    <FieldLabel label="Description">
                        <TextArea
                            value={formData.description || ''}
                            onChange={(e: ComponentEvent<string>) => setFormData({ ...formData, description: e.value })}
                            placeholder="Enter description"
                            rows={3}
                            disabled={saving}
                        />
                    </FieldLabel>

                    {hierarchical && (
                        <FieldLabel label="Parent" hint="Select a parent to create a hierarchy">
                            <Dropdown
                                options={parentOptions}
                                value={formData.parentId || ''}
                                onChange={(e: ComponentEvent<string>) => setFormData({ ...formData, parentId: e.value || undefined })}
                                disabled={saving}
                            />
                        </FieldLabel>
                    )}

                    {customFields.map((field) => (
                        <FieldLabel key={field.key} label={field.label} required={field.required}>
                            {field.type === 'text' && (
                                <TextInput
                                    value={(formData.metadata?.[field.key] as string) || ''}
                                    onChange={(e: ComponentEvent<string>) =>
                                        setFormData({
                                            ...formData,
                                            metadata: { ...formData.metadata, [field.key]: e.value },
                                        })
                                    }
                                    disabled={saving}
                                />
                            )}
                            {field.type === 'textarea' && (
                                <TextArea
                                    value={(formData.metadata?.[field.key] as string) || ''}
                                    onChange={(e: ComponentEvent<string>) =>
                                        setFormData({
                                            ...formData,
                                            metadata: { ...formData.metadata, [field.key]: e.value },
                                        })
                                    }
                                    rows={3}
                                    disabled={saving}
                                />
                            )}
                            {field.type === 'dropdown' && field.options && (
                                <Dropdown
                                    options={field.options}
                                    value={(formData.metadata?.[field.key] as string) || ''}
                                    onChange={(e: ComponentEvent<string>) =>
                                        setFormData({
                                            ...formData,
                                            metadata: { ...formData.metadata, [field.key]: e.value },
                                        })
                                    }
                                    disabled={saving}
                                />
                            )}
                            {field.type === 'color' && (
                                <div className="flex items-center gap-3">
                                    <input
                                        type="color"
                                        value={(formData.metadata?.[field.key] as string) || '#3B82F6'}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                metadata: { ...formData.metadata, [field.key]: e.target.value },
                                            })
                                        }
                                        disabled={saving}
                                        className="w-12 h-10 border border-slate-300 dark:border-slate-600 rounded cursor-pointer bg-white dark:bg-slate-700"
                                    />
                                    <TextInput
                                        value={(formData.metadata?.[field.key] as string) || ''}
                                        onChange={(e: ComponentEvent<string>) =>
                                            setFormData({
                                                ...formData,
                                                metadata: { ...formData.metadata, [field.key]: e.value },
                                            })
                                        }
                                        placeholder="#3B82F6"
                                        disabled={saving}
                                    />
                                </div>
                            )}
                            {field.type === 'icon' && (
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center justify-center w-10 h-10 shrink-0 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700">
                                        {(() => {
                                            const iconKey = formData.metadata?.[field.key] as string;
                                            const IconComponent = iconKey ? (fluxoIcons as Record<string, any>)[iconKey] : null;
                                            return IconComponent ? (
                                                <IconComponent className="w-5 h-5 text-slate-700 dark:text-slate-300" />
                                            ) : (
                                                <span className="text-xs text-slate-400">—</span>
                                            );
                                        })()}
                                    </div>
                                    <div className="flex-1">
                                        <Dropdown
                                            options={iconNameOptions}
                                            value={(formData.metadata?.[field.key] as string) || ''}
                                            onChange={(e: ComponentEvent<string>) =>
                                                setFormData({
                                                    ...formData,
                                                    metadata: { ...formData.metadata, [field.key]: e.value },
                                                })
                                            }
                                            disabled={saving}
                                        />
                                    </div>
                                </div>
                            )}
                        </FieldLabel>
                    ))}

                    <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={formData.isActive}
                                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                disabled={saving}
                                className="w-4 h-4 text-teal-600 bg-slate-100 border-slate-300 rounded focus:ring-teal-500 dark:focus:ring-teal-600 dark:ring-offset-slate-800 focus:ring-2 dark:bg-slate-700 dark:border-slate-600"
                            />
                            <span className="text-sm font-medium text-slate-900 dark:text-white">Active</span>
                        </label>
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <Button
                            label="Cancel"
                            leftIcon={TimesIcon}
                            variant="default"
                            layout="outlined"
                            onClick={() => setShowModal(false)}
                            disabled={saving}
                        />
                        <Button
                            label={editingItem ? 'Update' : 'Create'}
                            leftIcon={SaveIcon}
                            variant="primary"
                            onClick={handleSave}
                            isLoading={saving}
                            disabled={saving}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default MasterListEditor;
